import { Controller, FieldValues, useFormState } from 'react-hook-form';
import { BaseFormFieldProps } from './types';
import CategorySelector from '../selectors/CategorySelector';

export interface FormMultiCategorySelectorProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<BaseFormFieldProps<TFieldValues>, 'label' | 'placeholder'> {
  placeholder?: string;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  'aria-label'?: string;
  isDisabled?: boolean;
  parentOnly?: boolean;
}

const FormMultiCategorySelector = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  isRequired = false,
  showErrorOnTouched = true,
  placeholder,
  label,
  variant = 'bordered',
  className,
  'aria-label': ariaLabel,
  isDisabled = false,
  parentOnly = false,
  ...rest
}: FormMultiCategorySelectorProps<TFieldValues>) => {
  const { touchedFields, errors, isSubmitted } = useFormState({ control });
  const fieldError = errors[name]?.message as string | undefined;
  const isTouched = touchedFields[name];
  const showError = showErrorOnTouched ? (isTouched || isSubmitted) && !!fieldError : !!fieldError;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <CategorySelector
          value={field.value || []}
          onChange={field.onChange}
          isRequired={isRequired}
          isInvalid={showError}
          errorMessage={showError ? fieldError : undefined}
          placeholder={placeholder}
          label={label}
          variant={variant}
          className={className}
          aria-label={ariaLabel}
          selectionMode="multiple"
          isDisabled={isDisabled}
          parentOnly={parentOnly}
          {...rest}
        />
      )}
    />
  );
};

FormMultiCategorySelector.displayName = 'FormMultiCategorySelector';

export default FormMultiCategorySelector;
